import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { MoreHorizontal, Type } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarButton,
  CanvasToolbarDivider,
  canvasToolbarButtonVariants,
} from "../components/toolbar";
import { canvasToolbarIconClassName } from "../components/toolbar/canvasToolbarIcon";
import { ArrowEndPicker, type ArrowEndStyle } from "./shared/ArrowEndPicker";
import { EDGE_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import { LineStylePicker, type LineStyle } from "./shared/LineStylePicker";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

export type LineCurveType = "straight" | "smooth" | "step";

/** The style fields a line carries, semantic form (edges translate to RF's native fields). */
export interface LineStyleFields {
  color?: string;
  stroke_width?: number;
  stroke_style?: "solid" | "dashed" | "dotted";
  start_arrow?: ArrowEndStyle;
  end_arrow?: ArrowEndStyle;
  curve_type?: LineCurveType;
  /** @deprecated use start_arrow/end_arrow instead */
  arrow_mode?: "none" | "end" | "both";
}

/** Prior names, kept so the style translation and the last-style store read the same. */
export type ConnectorCurveType = LineCurveType;
export type EdgeStyle = LineStyleFields;
export type ConnectorStyle = LineStyleFields;

/** Curve icons — line shape only, no arrowheads. */
export const CURVE_TYPE_ICONS: Record<LineCurveType, React.ReactNode> = {
  straight: (
    <svg
      className={canvasToolbarIconClassName}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <line
        x1="2"
        y1="14"
        x2="14"
        y2="2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  step: (
    <svg
      className={canvasToolbarIconClassName}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <polyline
        points="1,13 7,13 7,3 14,3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  ),
  smooth: (
    <svg
      className={canvasToolbarIconClassName}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M 1 13 Q 2 3 14 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  ),
};

/** Design-system names: Straight · Elbow · Curved. */
const CURVE_OPTIONS: { value: LineCurveType; label: string }[] = [
  { value: "straight", label: "Straight" },
  { value: "step", label: "Elbow" },
  { value: "smooth", label: "Curved" },
];

interface LineFloatingToolbarProps {
  /** An attached edge, or a free connector (a node, with the node More menu). */
  variant: "edge" | "connector";
  style: LineStyleFields;
  onStyleChange: (patch: LineStyleFields) => void;
  onPopoverOpenChange?: (open: boolean) => void;
  hasLabel: boolean;
  onEditLabel: () => void;
  /** Edge only: More opens the edge's context menu, anchored to the button. */
  onOpenMenu?: (anchor: HTMLElement) => void;
}

/**
 * One bar for every line: Colour · Line style | Start arrow · Curve · End
 * arrow · Text | More. More opens the line's context menu — the node menu for
 * a connector, the edge menu for an edge — so the bar and right-click agree.
 */
export function LineFloatingToolbar({
  variant,
  style,
  onStyleChange,
  onPopoverOpenChange,
  hasLabel,
  onEditLabel,
  onOpenMenu,
}: LineFloatingToolbarProps) {
  const [isCurveOpen, setIsCurveOpen] = useState(false);
  const openRefs = useRef({
    color: false,
    line: false,
    start: false,
    end: false,
    curve: false,
  });

  const notify = useCallback(() => {
    onPopoverOpenChange?.(Object.values(openRefs.current).some(Boolean));
  }, [onPopoverOpenChange]);

  const track = useCallback(
    (key: keyof typeof openRefs.current) => (open: boolean) => {
      openRefs.current[key] = open;
      if (key === "curve") setIsCurveOpen(open);
      notify();
    },
    [notify],
  );

  const legacy = style.arrow_mode;
  const currentColor = style.color ?? "default";
  const currentStrokeStyle = style.stroke_style ?? "solid";
  const currentStrokeWidth = style.stroke_width ?? 2;
  const currentStartArrow: ArrowEndStyle =
    style.start_arrow ?? (legacy === "both" ? "arrow" : "none");
  const currentEndArrow: ArrowEndStyle =
    style.end_arrow ?? (legacy === "none" ? "none" : "arrow");
  const currentCurve: LineCurveType =
    style.curve_type ?? (variant === "edge" ? "step" : "straight");

  const rowClassName =
    "w-full px-3 py-1.5 flex items-center gap-2.5 rounded-lg transition-colors hover:bg-n-95/8 dark:hover:bg-n-95/10";

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame onMouseDown={(e) => e.preventDefault()}>
        <CanvasColorPickerButton
          colors={EDGE_COLORS}
          role="mark"
          activeValue={currentColor}
          tooltip={variant === "edge" ? "Edge color" : "Color"}
          onColorChange={(color) => onStyleChange({ color })}
          onOpenChange={track("color")}
          emitField="swatchHex"
        />

        <LineStylePicker
          value={{
            strokeStyle: currentStrokeStyle,
            strokeWidth: currentStrokeWidth,
          }}
          onChange={(ls: LineStyle) =>
            onStyleChange({
              stroke_style: ls.strokeStyle,
              stroke_width: ls.strokeWidth,
            })
          }
          onOpenChange={track("line")}
        />

        <CanvasToolbarDivider />

        <ArrowEndPicker
          value={currentStartArrow}
          onChange={(v) => onStyleChange({ start_arrow: v })}
          onOpenChange={track("start")}
          label="Start"
          isStart
        />

        <Popover open={isCurveOpen} onOpenChange={track("curve")}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  aria-label="Curve"
                  className={canvasToolbarButtonVariants()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {CURVE_TYPE_ICONS[currentCurve]}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Curve</TooltipContent>
          </Tooltip>
          <PopoverContent
            className="w-40 p-1 rounded-xl"
            align="center"
            side="top"
            sideOffset={14}
            onClick={(e) => e.stopPropagation()}
          >
            {CURVE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  onStyleChange({ curve_type: opt.value });
                  track("curve")(false);
                }}
                className={cn(
                  rowClassName,
                  currentCurve === opt.value &&
                    "bg-primary/10 text-primary font-medium",
                )}
              >
                {CURVE_TYPE_ICONS[opt.value]}
                <span className="text-xs">{opt.label}</span>
              </button>
            ))}
          </PopoverContent>
        </Popover>

        <ArrowEndPicker
          value={currentEndArrow}
          onChange={(v) => onStyleChange({ end_arrow: v })}
          onOpenChange={track("end")}
          label="End"
        />

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={hasLabel ? "Edit text" : "Add text"}
              onClick={(e) => {
                e.stopPropagation();
                onEditLabel();
              }}
              className={canvasToolbarButtonVariants()}
            >
              <Type className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {hasLabel ? "Edit text" : "Add text"}
          </TooltipContent>
        </Tooltip>

        <CanvasToolbarDivider />

        {variant === "connector" ? (
          <NodeToolbarContextMenuButton />
        ) : (
          <CanvasToolbarButton
            label="More"
            onClick={(e) => {
              e.stopPropagation();
              onOpenMenu?.(e.currentTarget);
            }}
          >
            <MoreHorizontal className="size-4" />
          </CanvasToolbarButton>
        )}
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
